import { Box, Button, CircularProgress, Grid, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EditExtraWorkAgreementNumber } from "./inputs/number";
import { SubmitHandler, useForm } from "react-hook-form";
import { ExtraWorkAgreementFormData, useValidateExtraWorkAgreementInput } from "./hooks/use-input-validation";
import { DocumentReferenceResponse, GetProjectResponse, useGetApiConfigQuery, useDeleteApiProjectsByProjectIdExtraworkagreementsAndExtraWorkAgreementIdDocumentsDocumentIdMutation } from "api/generatedApi";
import { api } from "api/enhancedEndpoints";
import { ScreenSizeEnum, useScreenSize } from "shared/use-screen-size";
import { useEffect, useState } from "react";
import { EditExtraWorkAgreementName } from "./inputs/name";
import { EditExtraWorkAgreementDocumentation } from "./inputs/documentation";
import { EditExtraWorkAgreementSelectType } from "./inputs/select-type";
import { EditExtraWorkAgreementStatus } from "./inputs/agreement-status";
import { EditExtraWorkAgreementTimeOrPayment } from "./inputs/time-or-payment";
import { useNavigate, useParams } from "react-router-dom";
import { UseGetExtraWorkAgreementById } from "./hooks/use-get-extra-work-agreement-by-id";
import { useDashboardRestrictions } from "shared/user-restrictions/use-dashboard-restrictions";
import { useCreateExtraWorkAgreement } from "./hooks/use-create-extra-work-agreement";
import { useUpdateExtraWorkAgreement } from "./hooks/use-update-extra-work-agreement";
import { useConfirm } from "components/shared/alert/confirm/hooks/use-confirm";
import { UploadDocument } from "shared/editors/hooks/upload-document";
import { useToast } from "shared/toast/hooks/use-toast";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch } from "react-redux";
import { useHistoryDialog } from "./hooks/use-history-dialog";
import { DesktopThreeStepNavigation } from "components/shared/three-step-navigation/desktop-three-step-navigation";
import { EditExtraWorkAgreementInternalNumber } from "./inputs/internal-number";
import { MobileOneStepBackBreadcrumb } from "./inputs/mobile-one-step-back";
import EventNoteIcon from "@mui/icons-material/EventNote";

interface Props {
  project: GetProjectResponse;
}

export const ExtraWorkAgreementCreatePage = (props: Props) => {
  const { project } = props;
  const { t } = useTranslation();
  const { screenSize } = useScreenSize();

  const { hasValidateInputs, isStateLockedBasedOnState, isLockedBasedOnState } = useValidateExtraWorkAgreementInput();
  const [editPageState, setEditPageState] = useState<"view" | "edit">("view");
  const [resetLocalDocumentSwitch, setResetLocalDocumentSwitch] = useState(false);
  const [cancelState, setCancelState] = useState<"create" | "edit">("create");
  const [isDirty, setIsDirty] = useState(false);

  // State for workItemStatus to determine if sub components should be allowed to be edited or not. True == editable, False == not editable
  const [workItemStatus, setWorkItemStatus] = useState(true);

  const [pageState, setPageState] = useState<"create" | "edit">("create");
  const [disableCreateButtonState, setDisableCreateButtonState] = useState(true);
  const [disableEditButtonState, setDisableEditButtonState] = useState(true);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [tempFilesforUpload, setTempFilesforUpload] = useState<File[]>([]);
  const [tempFilesForDelete, setTempFilesForDelete] = useState<DocumentReferenceResponse[]>([]);
  const [extraWorkAgreementId, setExtraWorkAgreementId] = useState<string | null>(useParams<"extraworkAgreementId">().extraworkAgreementId ?? null);
  const [isInputValid, setIsInputValid] = useState(false);
  const [mayNotChangeState, setMayNotChangeState] = useState(false);
  const navigate = useNavigate();
  const Toast = useToast();
  const restrictions = useDashboardRestrictions(project);
  const allowChanges = restrictions.canEditExtraWorkAgreement();
  const path = `/api/projects/${project.id}/extraworkagreements/${extraWorkAgreementId}/documents`;
  const toast = useToast();
  const dispatch = useDispatch();

  const handleSuccessEdit = () => {
    setEditPageState("view");
  };

  const { data: configData } = useGetApiConfigQuery();
  const uploadDocument = UploadDocument(path, configData?.maxUploadFileSizeMB);
  const createExtraWorkAgreement = useCreateExtraWorkAgreement({ project, tempFilesforUpload });
  const { agreement: fetchedAgreement, isLoading: isFetchingAgreement } = UseGetExtraWorkAgreementById({ projectId: project.id ?? "", agreementId: extraWorkAgreementId ?? "" });
  const updateExtraWorkAgreement = useUpdateExtraWorkAgreement({ project, agreementId: extraWorkAgreementId ?? "", onSuccess: handleSuccessEdit });
  const [deleteDocument] = useDeleteApiProjectsByProjectIdExtraworkagreementsAndExtraWorkAgreementIdDocumentsDocumentIdMutation();

  const openHistoryDialog = useHistoryDialog({
    auditLogEntries: fetchedAgreement?.logEntries || [],
  });

  const { register, setValue, getValues, handleSubmit, control, watch, formState, reset } = useForm<ExtraWorkAgreementFormData>({
    mode: "all",
    defaultValues: {
      extraWorkAgreementType: "CustomerHours",
      workTime: { hours: 0, minutes: 0 },
      approvalStatus: fetchedAgreement?.approvalStatus ?? "UnderDevelopment",
    },
  });

  const openCancelConfirmCreateDialog = useConfirm({
    title: t("dashboard.extraWorkAgreements.create.confirmCancelTitle"),
    description: t("dashboard.extraWorkAgreements.create.confirmCancelDescription"),
    submitButtonLabel: t("common.finish"),
    submit: () => navigate(-1),
  });

  const openCancelConfirmEditDialog = useConfirm({
    title: t("dashboard.extraWorkAgreements.update.confirmCancelTitle"),
    description: t("dashboard.extraWorkAgreements.update.confirmCancelDescription"),
    submitButtonLabel: t("common.finish"),
    submit: () => {
      setResetTrigger((prev) => prev + 1);
      setEditPageState("view");
      setResetLocalDocumentSwitch((prev) => !prev);
    },
  });

  useEffect(() => {
    if (extraWorkAgreementId) {
      setPageState("edit");
      const isStateLocked = isStateLockedBasedOnState(getValues);
      setMayNotChangeState(isStateLocked);
      setCancelState("edit");
    }
  }, [extraWorkAgreementId]);

  useEffect(() => {
    if (fetchedAgreement) {
      reset({
        extraWorkAgreementType: fetchedAgreement.extraWorkAgreementType,
        extraWorkAgreementNumber: fetchedAgreement.extraWorkAgreementNumber,
        name: fetchedAgreement.name,
        description: fetchedAgreement.description,
        workTime: fetchedAgreement.workTime,
        paymentDkr: fetchedAgreement.paymentDkr,
        isPrivate: fetchedAgreement.isPrivate,
        approvalStatus: fetchedAgreement.approvalStatus,
      });
    }
  }, [fetchedAgreement, resetTrigger]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (
        name === "extraWorkAgreementNumber" ||
        name === "name" ||
        name === "description" ||
        name === "extraWorkAgreementType" ||
        name === "paymentDkr" ||
        name === "workTime" ||
        name === "workTime.hours" ||
        name === "workTime.minutes" ||
        name === "approvalStatus"
      ) {
        const isValidated = hasValidateInputs(getValues);
        const isDirty = formState.isDirty;
        const isStateLocked = isStateLockedBasedOnState(getValues);
        setMayNotChangeState(isStateLocked);
        setIsInputValid(isValidated && isDirty);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, getValues, hasValidateInputs, setIsInputValid, formState.isDirty, tempFilesforUpload.length, tempFilesForDelete.length]);

  useEffect(() => {
    const hasFileChanges = tempFilesforUpload.length > 0 || tempFilesForDelete.length > 0;
    setIsDirty(formState.isDirty || tempFilesforUpload.length > 0 || tempFilesForDelete.length > 0);
    if (formState.isDirty || hasFileChanges) {
      if (isInputValid) {
        setDisableCreateButtonState(false);
      } else {
        setDisableCreateButtonState(true);
      }
    }
    if (formState.isDirty || hasFileChanges) {
      setDisableEditButtonState(false);
    }
  }, [formState.isDirty, isInputValid, tempFilesforUpload.length, tempFilesForDelete.length]);

  const onSubmitCreate: SubmitHandler<ExtraWorkAgreementFormData> = async (data) => {
    if (restrictions.canCreateExtraWorkAgreements()) {
      await createExtraWorkAgreement(data, "UnderDevelopment");

      //upload new documents
      for (const file of tempFilesforUpload) {
        await uploadDocument(file);
      }
      setTempFilesforUpload([]);

      //delete removed documents
      for (const document of tempFilesForDelete) {
        if (document.documentId) {
          await DocumentDelete(document);
        }
      }
      setTempFilesForDelete([]);
      navigate(-1);
    } else {
      Toast.error(t("dashboard.extraWorkAgreements.create.noPermission"));
    }
  };

  const onSubmitCreateAndSend: SubmitHandler<ExtraWorkAgreementFormData> = async (data) => {
    if (restrictions.canCreateExtraWorkAgreements()) {
      await createExtraWorkAgreement(data, "AwaitingApproval");
      //upload new documents
      for (const file of tempFilesforUpload) {
        await uploadDocument(file);
      }
      setTempFilesforUpload([]);

      //delete removed documents
      for (const document of tempFilesForDelete) {
        if (document.documentId) {
          await DocumentDelete(document);
        }
      }
      setTempFilesForDelete([]);
      dispatch(api.util.invalidateTags(["ExtraWorkAgreement"]));
      navigate(-1);
    } else {
      Toast.error(t("dashboard.extraWorkAgreements.create.noPermission"));
    }
  };

  const onSubmitEdit: SubmitHandler<ExtraWorkAgreementFormData> = async (data) => {
    if (restrictions.canEditExtraWorkAgreement()) {
      //update extra work agreement
      await updateExtraWorkAgreement(data);

      //upload new documents
      for (const file of tempFilesforUpload) {
        await uploadDocument(file);
      }
      setTempFilesforUpload([]);

      //delete removed documents
      for (const document of tempFilesForDelete) {
        if (document.documentId) {
          await DocumentDelete(document);
        }
      }
      setTempFilesForDelete([]);

      dispatch(api.util.invalidateTags(["ExtraWorkAgreement"]));
    } else {
      Toast.error(t("dashboard.extraWorkAgreements.update.noPermission"));
    }
  };

  const onCancel = (cancelState: "create" | "edit") => {
    if (formState.isDirty || tempFilesforUpload.length > 0 || tempFilesForDelete.length > 0) {
      if (cancelState === "edit") {
        openCancelConfirmEditDialog();
      } else if (cancelState === "create") {
        openCancelConfirmCreateDialog();
      }
    } else if (cancelState === "edit") {
      setEditPageState("view");
      setDisableCreateButtonState(true);
      setDisableEditButtonState(true);
    } else {
      navigate(-1);
    }
  };

  const handleEditClick = () => {
    setEditPageState("edit");
    setWorkItemStatus(fetchedAgreement?.approvalStatus === "UnderDevelopment" ? true : false);

    // Initially disable the edit button until changes are made
    setDisableEditButtonState(true);
  };

  if (isFetchingAgreement) {
    return (
      <Box sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress size={100} />
      </Box>
    );
  }

  const DocumentDelete = async (document: DocumentReferenceResponse) => {
    try {
      await deleteDocument({
        projectId: project.id ? project.id : "",
        documentId: document.documentId ? document.documentId : "",
        extraWorkAgreementId: extraWorkAgreementId ? extraWorkAgreementId : "",
      }).unwrap();
    } catch (e) {
      toast.error(t("content.overview.documents.deleteError", { documentName: document.name })); //todo: make translation
    }
  };

  const manageDisableComponent = () => {
    if (pageState === "create") {
      return false;
    }

    if (!allowChanges || editPageState === "view" || !workItemStatus) {
      return true;
    }
    if (allowChanges && editPageState === "edit" && workItemStatus) {
      return false;
    }
  };
  return (
    <Grid item xs={true} overflow={"auto"} padding={1} height={"100%"}>
      <Box sx={{ px: screenSize === ScreenSizeEnum.Mobile ? 1 : 5.25, py: screenSize === ScreenSizeEnum.Mobile ? 1 : 5.5 }}>
        <Box sx={{ position: "relative", display: "flex" }}>
          {pageState === "edit" && editPageState === "view" && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                zIndex: 1,
              }}
            >
              <IconButton onClick={openHistoryDialog} sx={{ pt: screenSize === ScreenSizeEnum.Mobile ? 1 : 0 }}>
                <EventNoteIcon />
              </IconButton>
              {!mayNotChangeState && allowChanges && (
                <IconButton
                  onClick={() => {
                    handleEditClick();
                  }}
                  sx={{ pt: screenSize === ScreenSizeEnum.Mobile ? 1 : 0 }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          )}
          {/*breadcrumb router, the one step has been kept for mobile because of lack of space*/}
          {screenSize === ScreenSizeEnum.Mobile ? (
            <MobileOneStepBackBreadcrumb from={t("dashboard.extraWorkAgreements.create.breadcrumbTitle2")} isDirty={isDirty} onClick={() => onCancel(cancelState)} />
          ) : (
            <DesktopThreeStepNavigation
              firstTitle={t("dashboard.extraWorkAgreements.create.breadcrumbTitle1")}
              secondTitle={t("dashboard.extraWorkAgreements.create.breadcrumbTitle2")}
              thirdTitle={pageState === "create" ? t("dashboard.extraWorkAgreements.create.breadcrumbTitle3") : fetchedAgreement?.name || ""}
              onClick={() => onCancel(cancelState)}
              isDirty={isDirty}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "auto", overflow: "visible", pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, overflow: "inherit" }}>
            {pageState === "edit" && <EditExtraWorkAgreementInternalNumber agreement={fetchedAgreement} />}
            <EditExtraWorkAgreementName register={register} agreement={fetchedAgreement} disabled={manageDisableComponent() ?? true} />
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
              <EditExtraWorkAgreementSelectType control={control} agreement={fetchedAgreement} disabled={manageDisableComponent() ?? true} />
              <EditExtraWorkAgreementTimeOrPayment watch={watch} getValue={getValues} setValue={setValue} register={register} agreement={fetchedAgreement} disabled={manageDisableComponent() ?? true} />
            </Box>
            <EditExtraWorkAgreementDocumentation
              register={register}
              getValue={getValues}
              setValue={setValue}
              agreement={fetchedAgreement}
              disabled={manageDisableComponent() ?? true}
              control={control}
              documents={fetchedAgreement?.documents ?? []}
              setTempFilesforUpload={setTempFilesforUpload}
              setTempFilesForDelete={setTempFilesForDelete}
              canEdit={restrictions.canEditExtraWorkAgreement()}
              resetLocalDocumentSwitch={resetLocalDocumentSwitch}
            />
            <EditExtraWorkAgreementNumber register={register} getValue={getValues} setValue={setValue} agreement={fetchedAgreement} disabled={manageDisableComponent() ?? true} />
            {pageState === "edit" && fetchedAgreement && <EditExtraWorkAgreementStatus register={register} watch={watch} agreement={fetchedAgreement} disabled={!allowChanges || mayNotChangeState || editPageState === "view"} />}
          </Box>

          {/*first set of buttons*/}
          {editPageState === "edit" ||
            (pageState === "create" && (
              <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                <Button variant="contained" color="primary" disabled={disableCreateButtonState} onClick={handleSubmit(onSubmitCreate)} sx={{ width: "auto" }}>
                  {t("dashboard.extraWorkAgreements.create.createButton")}
                </Button>
                <Button variant="contained" color="primary" disabled={disableCreateButtonState} onClick={handleSubmit(onSubmitCreateAndSend)} sx={{ width: "auto" }}>
                  {t("dashboard.extraWorkAgreements.create.createAndSendButton")}
                </Button>
                <Button variant="outlined" color="primary" onClick={() => onCancel("create")} sx={{ width: "auto" }}>
                  {t("dashboard.extraWorkAgreements.create.cancelButton")}
                </Button>
              </Box>
            ))}

          {/*second set of buttons*/}
          {editPageState === "edit" && pageState === "edit" && (
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
              <Button variant="contained" color="primary" disabled={disableEditButtonState} onClick={handleSubmit(onSubmitEdit)} sx={{ width: "auto" }}>
                {t("dashboard.extraWorkAgreements.create.updateButton")}
              </Button>
              <Button variant="outlined" color="primary" onClick={() => onCancel("edit")} sx={{ width: "auto" }}>
                {t("dashboard.extraWorkAgreements.create.updateCancelButton")}
              </Button>
            </Box>
          )}

          {screenSize === ScreenSizeEnum.Mobile && <Box sx={{ height: "60px" }} />}
        </Box>
      </Box>
    </Grid>
  );
};
